import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Search, X } from "react-feather";
import toast from "react-hot-toast";
import DataTable from "../../../../components/common/DataTable";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import {
  useListPettyCashVouchersQuery,
  useListPettyCashReturnsQuery,
  useRecordPettyCashVoucherReturnMutation,
  useGetPettyCashCategoriesQuery,
} from "../../pettyCashApi";
import { useCanRecordReturn } from "../../usePettyCashPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const money = (n) => Number(n || 0).toFixed(2);
const dateOnly = (v) => (v ? new Date(v).toLocaleDateString() : "-");

// Typing in a search box shouldn't fire a request per keystroke.
const useDebounced = (value, ms = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
};

// Voucher Returns (Batch168). Unspent cash handed back after an expense goes
// back to the fund the voucher was paid from, under the voucher's category.
// Top: record one - find the paid voucher with real filters (the server
// searches voucher no, description, requester, paid-to, supplier, receipt no
// and payment ref; only vouchers with something left to return are listed),
// pick it, enter the amount. Bottom: every return recorded so far.
const VoucherReturnsPage = () => {
  const canRecordReturn = useCanRecordReturn();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  // ---- Picker (paid vouchers with a returnable balance) ----
  const [showPicker, setShowPicker] = useState(false);
  const picker = useDatePresetRange("THIS_YEAR");
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerCategoryId, setPickerCategoryId] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const debouncedPickerSearch = useDebounced(pickerSearch);
  const debouncedMin = useDebounced(minAmount);
  const debouncedMax = useDebounced(maxAmount);
  const [selected, setSelected] = useState(null);
  const [returnAmount, setReturnAmount] = useState("");
  const [remarks, setRemarks] = useState("");

  const { data: categories = [] } = useGetPettyCashCategoriesQuery();
  const { data: candidateData, isFetching: isFetchingCandidates } = useListPettyCashVouchersQuery(
    {
      branchId: selectedBranchId ?? undefined,
      status: "PAID",
      returnableOnly: true,
      search: debouncedPickerSearch.trim() || undefined,
      categoryId: pickerCategoryId || undefined,
      minAmount: debouncedMin !== "" ? debouncedMin : undefined,
      maxAmount: debouncedMax !== "" ? debouncedMax : undefined,
      dateFrom: picker.dateFrom || undefined,
      dateTo: picker.dateTo || undefined,
      pageSize: 100,
    },
    { skip: !showPicker }
  );
  const candidates = candidateData?.items || [];
  const candidatesTruncated = (candidateData?.totalCount ?? 0) > candidates.length;

  const [recordReturn, { isLoading: isSaving }] = useRecordPettyCashVoucherReturnMutation();

  const returnableOf = (v) => Math.max(Number(v.paidAmount || 0) - Number(v.returnedAmount || 0), 0);

  const pick = (v) => {
    setSelected(v);
    setReturnAmount("");
    setRemarks("");
  };

  const clearPickerFilters = () => {
    setPickerSearch("");
    setPickerCategoryId("");
    setMinAmount("");
    setMaxAmount("");
  };

  const handleSave = async () => {
    if (!selected) return;
    const amount = Number(returnAmount);
    const max = returnableOf(selected);
    if (!(amount > 0)) return toast.error("Enter the amount being returned");
    if (amount > max) return toast.error(`At most ${money(max)} can be returned on ${selected.voucherNumber}`);
    if (!window.confirm(`Return ${money(amount)} to ${selected.fundName || "the fund"} against ${selected.voucherNumber}? This cannot be undone.`)) return;
    try {
      const result = await recordReturn({ recordGuid: selected.recordGuid, returnAmount: amount, remarks: remarks || undefined }).unwrap();
      toast.success(`Return ${result?.returnNumber || ""} recorded`);
      setSelected(null);
      setReturnAmount("");
      setRemarks("");
    } catch (e) {
      toast.error(e?.message || "Could not record the return");
    }
  };

  // ---- Register (returns already recorded) ----
  const register = useDatePresetRange("THIS_MONTH");
  const [registerSearch, setRegisterSearch] = useState("");
  const debouncedRegisterSearch = useDebounced(registerSearch);
  const { data: returnsData, isLoading: isLoadingReturns } = useListPettyCashReturnsQuery({
    branchId: selectedBranchId ?? undefined,
    dateFrom: register.dateFrom || undefined,
    dateTo: register.dateTo || undefined,
    search: debouncedRegisterSearch.trim() || undefined,
    pageSize: 200,
  });
  const returns = returnsData?.items || [];
  const returnsTruncated = (returnsData?.totalCount ?? 0) > returns.length;
  const totalReturned = returns.reduce((s, r) => s + Number(r.returnAmount || 0), 0);

  const candidateColumns = [
    {
      title: "Voucher",
      dataIndex: "voucherNumber",
      render: (v, r) => <Link to={paths.pettyCashVoucherDetail(r.recordGuid)}>{v}</Link>,
    },
    { title: "Date", dataIndex: "expenseDate", render: dateOnly },
    {
      title: "Description",
      dataIndex: "description",
      width: 240,
      render: (v) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", minWidth: 160, maxWidth: 240, lineHeight: 1.35 }}>{v || "-"}</div>
      ),
    },
    { title: "Requested by", dataIndex: "requestedByName", render: (v) => v || "-" },
    { title: "Paid to", dataIndex: "paidToEmployeeName", render: (v, r) => v || r.paidToPersonName || "-" },
    { title: "Category", dataIndex: "categoryName", render: (v) => v || "-" },
    { title: "Paid", dataIndex: "paidAmount", render: (v) => money(v) },
    { title: "Returned", dataIndex: "returnedAmount", render: (v) => (Number(v || 0) > 0 ? money(v) : "-") },
    { title: "Can return", key: "returnable", render: (_, r) => <strong>{money(returnableOf(r))}</strong> },
    {
      title: "",
      key: "pick",
      render: (_, r) => (
        <button
          type="button"
          className={`btn btn-sm ${selected?.recordGuid === r.recordGuid ? "btn-success" : "btn-outline-success"}`}
          onClick={() => pick(r)}
        >
          {selected?.recordGuid === r.recordGuid ? "Selected" : "Select"}
        </button>
      ),
    },
  ];

  const returnColumns = [
    { title: "Return No", dataIndex: "returnNumber" },
    { title: "Date", dataIndex: "returnDate", render: (v) => (v ? new Date(v).toLocaleString() : "-") },
    {
      title: "Voucher",
      dataIndex: "voucherNumber",
      render: (v, r) => <Link to={paths.pettyCashVoucherDetail(r.voucherRecordGuid)}>{v}</Link>,
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Fund", dataIndex: "fundName" },
    { title: "Category", dataIndex: "categoryName", render: (v) => v || "-" },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (v) => <div style={{ whiteSpace: "normal", maxWidth: 260 }}>{v || "-"}</div>,
    },
    { title: "Recorded by", dataIndex: "createdByName", render: (v) => v || "-" },
    { title: "Amount", dataIndex: "returnAmount", render: (v) => <strong>{money(v)}</strong> },
  ];

  const exportColumns = [
    { header: "Return No", value: (r) => r.returnNumber },
    { header: "Date", value: (r) => dateOnly(r.returnDate) },
    { header: "Voucher", value: (r) => r.voucherNumber },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Fund", value: (r) => r.fundName },
    { header: "Category", value: (r) => r.categoryName || "-" },
    { header: "Remarks", value: (r) => r.remarks || "" },
    { header: "Recorded by", value: (r) => r.createdByName || "" },
    { header: "Amount", value: (r) => money(r.returnAmount), align: "right" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Voucher Returns</h4>
              <h6>Unspent cash handed back against paid vouchers - credited to the fund it was paid from</h6>
            </div>
          </div>
          {canRecordReturn && (
            <div className="page-btn">
              <button
                type="button"
                className="btn btn-added d-inline-flex align-items-center gap-1"
                onClick={() => {
                  setShowPicker((s) => !s);
                  setSelected(null);
                }}
              >
                {showPicker ? <X size={16} /> : <RotateCcw size={16} />} {showPicker ? "Close" : "Record Return"}
              </button>
            </div>
          )}
        </div>

        {showPicker && canRecordReturn && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-1">1. Find the paid voucher</h5>
              <p className="text-muted small mb-3">
                Only paid vouchers with some amount still not returned are listed
                {branchCode ? ` (branch ${branchCode})` : ""}. The date filter is the voucher's expense date.
              </p>
              <div className="d-flex flex-column gap-2 mb-3">
                <DateRangePicker {...picker} />
                <div className="row g-2 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label small mb-1">Search</label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text"><Search size={14} /></span>
                      <input
                        type="text"
                        className="form-control"
                        value={pickerSearch}
                        onChange={(e) => setPickerSearch(e.target.value)}
                        placeholder="Voucher no, description, requester, paid to, supplier, receipt no"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Category</label>
                    <select className="form-control form-control-sm" value={pickerCategoryId} onChange={(e) => setPickerCategoryId(e.target.value)}>
                      <option value="">All categories</option>
                      {categories.map((c) => (
                        <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6 col-md-1">
                    <label className="form-label small mb-1">Min amount</label>
                    <input type="number" min="0" className="form-control form-control-sm" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} />
                  </div>
                  <div className="col-6 col-md-1">
                    <label className="form-label small mb-1">Max amount</label>
                    <input type="number" min="0" className="form-control form-control-sm" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary w-100" onClick={clearPickerFilters}>
                      Clear filters
                    </button>
                  </div>
                </div>
              </div>
              {candidatesTruncated && (
                <div className="alert alert-warning py-2 px-3 mb-2">
                  Showing the first {candidates.length} of {candidateData.totalCount} matches - narrow the search to find the voucher.
                </div>
              )}
              <div className="table-responsive">
                <DataTable
                  columns={candidateColumns}
                  dataSource={candidates}
                  rowKey={(r) => r.recordGuid}
                  loading={isFetchingCandidates}
                />
              </div>

              {selected && (
                <div className="border rounded-3 p-3 mt-3 bg-light">
                  <h5 className="mb-1">2. Record the return</h5>
                  <p className="small mb-3">
                    <strong>{selected.voucherNumber}</strong> &middot; {selected.description} &middot; paid{" "}
                    {money(selected.paidAmount)}
                    {Number(selected.returnedAmount || 0) > 0 ? `, already returned ${money(selected.returnedAmount)}` : ""}.
                    Goes back to <strong>{selected.fundName || "the fund it was paid from"}</strong>
                    {selected.categoryName ? <> under <strong>{selected.categoryName}</strong></> : null}; up to{" "}
                    <strong>{money(returnableOf(selected))}</strong> can be returned.
                  </p>
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Amount returned</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        max={returnableOf(selected)}
                        className="form-control"
                        value={returnAmount}
                        onChange={(e) => setReturnAmount(e.target.value)}
                        placeholder={money(returnableOf(selected))}
                      />
                    </div>
                    <div className="col-md-9 mb-3">
                      <label className="form-label">Remarks (optional)</label>
                      <input
                        type="text"
                        maxLength={300}
                        className="form-control"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="e.g. change from fuel purchase"
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-success" onClick={handleSave} disabled={isSaving}>
                      Confirm Return
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="row mb-3">
          <div className="col-6 col-md-3">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Returns</div>
              <div className="fs-5 fw-bold">{returnsData?.totalCount ?? 0}</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Returned to funds</div>
              <div className="fs-5 fw-bold text-success">{money(totalReturned)}</div>
            </div></div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2 mb-2">
              <DateRangePicker {...register} />
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="input-group input-group-sm" style={{ maxWidth: 380 }}>
                  <span className="input-group-text"><Search size={14} /></span>
                  <input
                    type="text"
                    className="form-control"
                    value={registerSearch}
                    onChange={(e) => setRegisterSearch(e.target.value)}
                    placeholder="Return no, voucher no, description, remarks, paid to"
                  />
                </div>
                <ListExportToolbar
                  title="Petty Cash Voucher Returns"
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${register.dateFrom} to ${register.dateTo}`]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={returns}
                  fileName="PettyCashVoucherReturns"
                />
              </div>
            </div>
            {returnsTruncated && (
              <div className="alert alert-warning py-2 px-3 mb-2">
                Showing the first {returns.length} of {returnsData.totalCount} returns - narrow the date range for the full list.
              </div>
            )}
            <div className="table-responsive">
              <DataTable columns={returnColumns} dataSource={returns} rowKey={(r) => r.recordGuid} loading={isLoadingReturns} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherReturnsPage;
